import React, { useState, useCallback, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';

import { SectionHeader } from '@/components/common';
import { useUpdateVideo } from '@/hooks/videos.hook';
import { successToast, errorToast, confirmToast } from '@/common/toast';

/**
 * 동영상 수정 모달 컴포넌트
 * 
 * 동영상 정보 수정 및 자료 링크 관리 모달입니다.
 * 
 * @param {Object} props
 * @param {boolean} props.open - 모달 열림 상태
 * @param {Function} props.onClose - 모달 닫기 핸들러
 * @param {Object} props.video - 수정할 동영상 데이터
 * @param {Function} props.onSuccess - 수정 성공 후 콜백
 */
const VideoEditModal = ({
  open,
  onClose,
  video,
  onSuccess,
}) => {
  const [uploadProgress, setUploadProgress] = useState(false);

  // 동영상 수정 훅
  const updateVideoMutation = useUpdateVideo();

  // 폼 관리
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm({
    defaultValues: {
      videoName: '',
      videoSeq: 1,
      videoIntro: '',
      videoObject: '',
      attachedLink: '',
    },
  });

  // 동영상 데이터 로드 시 폼에 값 설정
  useEffect(() => {
    if (open && video) {
      reset({
        courseCategory: video.courseCategory || '',
        courseId: video.courseId || '',
        mediaId: video.mediaId || '',
        videoName: video.videoName || '',
        videoSeq: video.videoSeq || 1,
        videoIntro: video.videoIntro || '',
        videoObject: video.videoObject || '',
        attachedLink: video.attachedLink || null,
      });
    }
  }, [open, video, reset]);

  /**
   * 동영상 정보 수정 핸들러
   */
  const onSubmit = useCallback(
    async (data) => {
      if (!video) {
        errorToast('동영상 정보를 찾을 수 없습니다.');
        return;
      }

      setUploadProgress(true);
      try {
        await updateVideoMutation.mutateAsync({
          courseCategory: video.courseCategory,
          courseId: video.courseId,
          mediaId: video.mediaId,
          videoId: parseInt(video.videoId),
          videoName: data.videoName,
          videoSeq: parseInt(data.videoSeq),
          videoIntro: data.videoIntro || '',
          videoObject: data.videoObject || '',
          attachedLink: data.attachedLink || null,
        });

        successToast('동영상 정보가 수정되었습니다.');
        onSuccess?.();
        onClose();
      } catch (error) {
        errorToast('동영상 정보 수정에 실패했습니다.');
        console.error('동영상 수정 오류:', error);
      } finally {
        setUploadProgress(false);
      }
    },
    [video, updateVideoMutation, onSuccess, onClose]
  );


  /**
   * 모달 닫기 핸들러
   */
  const handleClose = useCallback(() => {
    if (!uploadProgress) {
      onClose();
    }
  }, [uploadProgress, onClose]);

  if (!video) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        동영상 수정
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)} id="video-edit-form">
          <Stack spacing={3}>
            {/* 과정 정보 */}
            <Box>
              <SectionHeader sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  동영상 정보
                </Typography>
              </SectionHeader>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    동영상 ID
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {video.videoId}
                  </Typography>
                </Box>
                {video.mediaId && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      미디버스 미디어 ID
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {video.mediaId}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* 동영상 정보 수정 */}
            <Box>
              <SectionHeader sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  수정 정보
                </Typography>
              </SectionHeader>
              <Stack spacing={2}>
                {/* 동영상명 */}
                <Controller
                  name="videoName"
                  control={control}
                  rules={{
                    required: '동영상명을 입력해주세요.',
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="동영상명 *"
                      placeholder="동영상명을 입력하세요"
                      error={!!errors.videoName}
                      helperText={errors.videoName?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* 동영상 순서 */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Controller
                    name="videoSeq"
                    control={control}
                    rules={{
                      required: '동영상 순서를 입력해주세요.',
                      min: { value: 1, message: '순서는 1 이상이어야 합니다.' },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="number"
                        label="동영상 순서 *"
                        placeholder="1"
                        error={!!errors.videoSeq}
                        helperText={errors.videoSeq?.message}
                        disabled={uploadProgress}
                        sx={{ width: '200px' }}
                      />
                    )}
                  />
                </Box>

                {/* 동영상 소개 */}
                <Controller
                  name="videoIntro"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="동영상 소개"
                      placeholder="동영상에 대한 간단한 소개를 입력하세요"
                      multiline
                      rows={3}
                      error={!!errors.videoIntro}
                      helperText={errors.videoIntro?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* 동영상 목표 */}
                <Controller
                  name="videoObject"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="동영상 목표"
                      placeholder="이 동영상을 통해 학습할 목표를 입력하세요"
                      multiline
                      rows={3}
                      error={!!errors.videoObject}
                      helperText={errors.videoObject?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />

                {/* 첨부 링크 */}
                <Controller
                  name="attachedLink"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="첨부 링크"
                      placeholder="관련 자료 링크를 입력하세요 (예: https://example.com)"
                      error={!!errors.attachedLink}
                      helperText={errors.attachedLink?.message}
                      disabled={uploadProgress}
                      fullWidth
                    />
                  )}
                />
              </Stack>
            </Box>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploadProgress}>
          취소
        </Button>
        <Button
          type="submit"
          form="video-edit-form"
          variant="contained"
          startIcon={uploadProgress ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={uploadProgress || !isDirty}
        >
          {uploadProgress ? '저장 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VideoEditModal;
